import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { router } from '@/app/router/router'
import { authQueryOptions } from '@/features/auth/api/auth/queries/auth-query'
import { bootstrapQueryKey } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import { getAccessToken, getRefreshToken } from '@/shared/lib/auth-tokens'
import { refreshAuthTokens } from '@/shared/lib/refresh-auth-tokens'
import {
  subscribeToAuthSessionChanges,
  type AuthSessionChangeType,
} from '@/shared/lib/auth-session-sync'
import { resolvePreferredLanguage } from '@/shared/i18n/supported-languages'

type LayoutKind = 'public' | 'full' | 'main'

function resolveCurrentLayout(): LayoutKind {
  for (let index = router.state.matches.length - 1; index >= 0; index -= 1) {
    const layout = (
      router.state.matches[index].staticData as
        | { layout?: LayoutKind }
        | undefined
    )?.layout

    if (layout) {
      return layout
    }
  }

  return router.state.matches.length > 1 ? 'main' : 'public'
}

function isProtectedRoute(): boolean {
  return resolveCurrentLayout() !== 'public'
}

export function useAuthSessionSync(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const handleTokensSet = async (): Promise<void> => {
      queryClient.removeQueries({ queryKey: bootstrapQueryKey })

      try {
        await queryClient.fetchQuery(authQueryOptions())
      } catch {
        queryClient.setQueryData(['auth'], null)
      }

      router.invalidate()
    }

    const handleTokensCleared = (): void => {
      queryClient.setQueryData(['auth'], null)
      void queryClient.cancelQueries({ queryKey: bootstrapQueryKey })
      queryClient.removeQueries({ queryKey: bootstrapQueryKey })

      router.invalidate()

      if (isProtectedRoute()) {
        router.navigate({
          to: '/$lang/login',
          params: { lang: resolvePreferredLanguage() },
        })
      }
    }

    const handleAuthSessionChange = (type: AuthSessionChangeType): void => {
      if (type === 'tokens-set') {
        void handleTokensSet()
        return
      }

      handleTokensCleared()
    }

    const unsubscribe = subscribeToAuthSessionChanges(handleAuthSessionChange)

    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return
      }

      const hasToken = Boolean(getAccessToken())
      const authData = queryClient.getQueryData(['auth'])

      if (!hasToken && authData) {
        if (getRefreshToken()) {
          // Silent renewal of an expired access token for the SAME session.
          // refreshAuthTokens() rotates the cookies and bumps the tokens
          // revision; useAuth then re-syncs and invalidates routes in the
          // background. Do NOT route through handleTokensSet here — it would
          // removeQueries(bootstrap), blanking every useBootstrapQuery consumer
          // (header, sidebar, language, trial banner...) and flickering the
          // whole shell on tab refocus.
          void refreshAuthTokens().catch(() => handleTokensCleared())
          return
        }

        handleTokensCleared()
        return
      }

      if (hasToken && !authData) {
        void handleTokensSet()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      unsubscribe()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [queryClient])
}
